// hooks/useArtifactPanel.ts
import { useState, useCallback, useMemo, useEffect } from 'react';
import { useArtifactStore } from '@/stores/artifact-store';
import { ArtifactItem, ArtifactLoadingPlaceholder } from '@/types/Artifact';
import { downloadChart, copyChartToClipboard, downloadTableAsCsv, copyTableToClipboard } from '@/lib/artifact-utils';
import { useIsMobile } from './use-mobile';

interface UseArtifactPanelProps {
  threadId: string;
}

export const useArtifactPanel = ({ threadId }: UseArtifactPanelProps) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (isMobile) {
      setIsFullscreen(true);
    }
  }, [isMobile])
  
  const {
    isOpen,
    getAllThreadItems,
    activeArtifactId,
    artifacts,
    hiddenArtifactIds,
    closePanel,
    setActiveArtifact,
    removeArtifact,
    cancelLoading,
    panelWidth,
    setPanelWidth
  } = useArtifactStore();

  // Get thread items
  const threadItems = useMemo(
    () => getAllThreadItems(threadId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getAllThreadItems, threadId, artifacts, hiddenArtifactIds]
  );
  
  // Find active item
  const activeItem = useMemo(
    () => activeArtifactId 
      ? threadItems.find(item => item.id === activeArtifactId) 
      : threadItems[threadItems.length - 1], // Use the newest artifact (last in sorted array)
    [activeArtifactId, threadItems]
  );

  // Handle resize callbacks
  const handleResizeStart = useCallback(() => {
    setIsResizing(true);
  }, []);

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
  }, []);

  const handleResize = useCallback((widthPercent: number) => {
    setPanelWidth(widthPercent);
  }, [setPanelWidth]);

  // Handle fullscreen toggle
  const handleToggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
  }, []);

  // Handle removing items
  const handleRemoveItem = useCallback((item: ArtifactItem | ArtifactLoadingPlaceholder) => {
    if ('isLoading' in item && item.isLoading) {
      cancelLoading(item.id);
      removeArtifact(item.id);
    } else {
      removeArtifact(item.id);
    }
  }, [cancelLoading, removeArtifact]);

  // Chart-specific actions
  const handleDownloadChart = useCallback(async () => {
    if (activeItem && !('isLoading' in activeItem) && activeItem.type === 'graph') {
      await downloadChart(activeItem.id);
    }
  }, [activeItem]);

  const handleCopyChart = useCallback(async () => {
    if (activeItem && !('isLoading' in activeItem) && activeItem.type === 'graph') {
      await copyChartToClipboard(activeItem.id);
    }
  }, [activeItem]);

  // Table-specific actions
  const handleDownloadTable = useCallback(() => {
    if (activeItem && !('isLoading' in activeItem) && activeItem.type === 'table') {
      downloadTableAsCsv(activeItem);
    }
  }, [activeItem]);

  const handleCopyTable = useCallback(async () => {
    if (activeItem && !('isLoading' in activeItem) && activeItem.type === 'table') {
      await copyTableToClipboard(activeItem);
    }
  }, [activeItem]);

  return {
    // State
    isOpen,
    isFullscreen,
    isResizing,
    panelWidth,
    threadItems,
    activeItem,
    activeArtifactId,
    
    // Actions
    closePanel,
    setActiveArtifact,
    handleRemoveItem,
    handleToggleFullscreen,
    handleResize,
    handleResizeStart,
    handleResizeEnd,
    handleDownloadChart,
    handleCopyChart,
    handleDownloadTable,
    handleCopyTable,
  };
};